
import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/auth";
import { Skeleton } from "@/components/ui/skeleton";

type VerificationAction = "verified" | "rejected" | "pending";

interface VerificationRecord {
  id: string;
  user_id: string | null; // Made nullable to support anonymized records
  document_id: string;
  action: VerificationAction;
  notes?: string;
  verified_by: string;
  created_at: string;
  verified_by_profile: { name: string };
  document_number?: string;
  document_name?: string;
  issue_date?: string;
  expiry_date?: string;
}

export function VerificationHistoryTable({ userId }: { userId?: string }) {
  const { user, profile } = useAuth();
  const [history, setHistory] = useState<VerificationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const targetUserId = userId || user?.id;

  useEffect(() => {
    if (!targetUserId) return;

    const fetchVerificationHistory = async () => {
      setLoading(true);
      try {
        // Get the verification history
        const { data, error } = await supabase.rpc(
          'get_verification_history',
          { user_id_param: targetUserId }
        );

        if (error) throw error;

        // For each history record, get the associated document details
        const historyWithDocuments = await Promise.all(
          (data || []).map(async (record: any) => {
            try {
              // Skip document lookup for anonymized records
              if (!record.user_id) {
                return {
                  ...record,
                  document_name: 'Anonymized Document',
                  document_number: null,
                  issue_date: null,
                  expiry_date: null,
                  verified_by_profile: typeof record.verified_by_profile === 'string' 
                    ? JSON.parse(record.verified_by_profile) 
                    : record.verified_by_profile
                };
              }

              const { data: docData, error: docError } = await supabase
                .from('user_id_documents')
                .select(`
                  document_number,
                  issue_date,
                  expiry_date,
                  id_document_types:document_type_id (
                    document_name
                  )
                `)
                .eq('id', record.document_id)
                .single();

              if (docError) throw docError;

              return {
                ...record,
                document_number: docData?.document_number,
                document_name: docData?.id_document_types?.document_name,
                issue_date: docData?.issue_date,
                expiry_date: docData?.expiry_date,
                verified_by_profile: typeof record.verified_by_profile === 'string' 
                  ? JSON.parse(record.verified_by_profile) 
                  : record.verified_by_profile
              };
            } catch (err) {
              console.error('Error fetching document details:', err);
              return {
                ...record,
                document_name: record.user_id ? 'Document Details Unavailable' : 'Anonymized Document',
                verified_by_profile: typeof record.verified_by_profile === 'string' 
                  ? JSON.parse(record.verified_by_profile) 
                  : record.verified_by_profile
              };
            }
          })
        );

        setHistory(historyWithDocuments);
      } catch (err: any) {
        console.error('Error fetching verification history:', err);
        setError(err.message || 'Failed to fetch verification history');
      } finally {
        setLoading(false);
      }
    };

    fetchVerificationHistory();
  }, [targetUserId]);

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Verification History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-destructive">Error: {error}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Verification History</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : history.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Document</TableHead>
                <TableHead>Action</TableHead>
                <TableHead className="hidden md:table-cell">Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.map((record) => (
                <TableRow key={record.id}>
                  <TableCell className="whitespace-nowrap">
                    {format(new Date(record.created_at), "MMM d, yyyy")}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{record.document_name || 'ID Document'}</div>
                    {record.document_number && (
                      <div className="text-xs text-muted-foreground">
                        ID# {record.document_number}
                      </div>
                    )}
                    {!record.user_id && (
                      <div className="text-xs text-muted-foreground italic">
                        (Anonymized Record)
                      </div>
                    )}
                    <div className="flex gap-2 mt-1">
                      {record.issue_date && (
                        <span className="text-xs text-muted-foreground">
                          Issued: {format(new Date(record.issue_date), "MM/dd/yyyy")}
                        </span>
                      )}
                      {record.expiry_date && (
                        <span className="text-xs text-muted-foreground">
                          Expires: {format(new Date(record.expiry_date), "MM/dd/yyyy")}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge 
                      className={
                        record.action === "verified" ? "bg-green-500" :
                        record.action === "rejected" ? "bg-destructive" :
                        "bg-muted"
                      }
                    >
                      {record.action.charAt(0).toUpperCase() + record.action.slice(1)}
                    </Badge>
                    <div className="text-xs text-muted-foreground mt-1">
                      by {record.verified_by_profile?.name || 'Admin'}
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                    {record.notes || "No notes"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            No verification history found
          </div>
        )}
      </CardContent>
    </Card>
  );
}
